<template>
  <div v-if="trip" class="trip-detail-layout">
    <!-- 頂部列 -->
    <div class="top-bar">
      <router-link to="/" class="back-btn">‹</router-link>
      <div class="top-info">
        <div class="top-name">{{ trip.coverEmoji }} {{ trip.name }}</div>
        <div class="top-sub">
          <StageTag :stage="stage" />
          <span class="muted">{{ trip.destinations.join(' · ') }}</span>
        </div>
      </div>
      <button class="icon-btn" @click="showDevPanel = true">⚙️</button>
    </div>

    <!-- 桌面版側欄 -->
    <div class="body">
      <aside class="desktop-nav">
        <router-link
          v-for="item in navItems" :key="item.name"
          :to="{ name: item.name }"
          class="nav-item" active-class="active"
        >
          <span class="icon">{{ item.icon }}</span>{{ item.label }}
        </router-link>
      </aside>

      <main class="content">
        <router-view />
      </main>
    </div>

    <!-- 手機版底部 tab -->
    <nav class="mobile-tabbar">
      <router-link
        v-for="name in mobilePrimary" :key="name"
        :to="{ name }" class="tab-item" active-class="active"
      >
        <span class="icon">{{ navByName(name).icon }}</span>
        <span class="label">{{ navByName(name).label }}</span>
      </router-link>
      <button class="tab-item" :class="{ active: moreActive }" @click="showMore = true">
        <span class="icon">⋯</span>
        <span class="label">更多</span>
      </button>
    </nav>

    <!-- 快速記錄 FAB -->
    <button class="fab" @click="openCapture">＋</button>

    <QuickCaptureSheet
      ref="capture" :trip-id="tripId" :creator-id="viewingAsMemberId"
      @open-discovery="openDiscovery" @open-expense="showExpense = true"
    />
    <DiscoveryFlow ref="discovery" :trip-id="tripId" :creator-id="viewingAsMemberId" />

    <van-popup v-model="showExpense" position="bottom" round :style="{ maxHeight: '92%' }" closeable>
      <div class="popup-title">記一筆帳</div>
      <ExpenseForm :trip-id="tripId" :creator-id="viewingAsMemberId" :preset-payer-id="viewingAsMemberId" @done="showExpense = false" @cancel="showExpense = false" />
    </van-popup>

    <van-action-sheet v-model="showMore" title="更多功能" :actions="moreActions" @select="onSelectMore" cancel-text="取消" />

    <!-- Demo 專用控制面板：切換檢視身分／模擬離線，方便展示可見性與離線同步 -->
    <van-popup v-model="showDevPanel" position="bottom" round>
      <DevPanel :trip-id="tripId" @close="showDevPanel = false" />
    </van-popup>
  </div>
  <div v-else class="empty-state">
    <span class="emoji">🧭</span>找不到這個 Trip
    <div style="margin-top: 12px;"><router-link to="/">回到 Trip 列表</router-link></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import StageTag from '@/components/trip/StageTag.vue'
import QuickCaptureSheet from '@/components/capture/QuickCaptureSheet.vue'
import DiscoveryFlow from '@/components/capture/DiscoveryFlow.vue'
import ExpenseForm from '@/components/finance/ExpenseForm.vue'
import DevPanel from '@/components/trip/DevPanel.vue'
import { NAV_ITEMS, MOBILE_PRIMARY } from '@/components/trip/tripNav'

export default {
  name: 'TripDetailLayout',
  components: { StageTag, QuickCaptureSheet, DiscoveryFlow, ExpenseForm, DevPanel },
  props: {
    id: { type: String, required: true }
  },
  data () {
    return {
      navItems: NAV_ITEMS,
      mobilePrimary: MOBILE_PRIMARY,
      showMore: false,
      showExpense: false,
      showDevPanel: false
    }
  },
  computed: {
    ...mapGetters({ tripById: 'trip/byId', tripStage: 'tripStage' }),
    tripId () {
      return this.id
    },
    trip () {
      return this.tripById(this.id)
    },
    stage () {
      return this.tripStage(this.id)
    },
    viewingAsMemberId () {
      return this.$store.state.ui.viewingAsMemberId
    },
    moreActive () {
      const moreNames = this.navItems.map(n => n.name).filter(n => !this.mobilePrimary.includes(n))
      return moreNames.includes(this.$route.name)
    },
    moreActions () {
      return this.navItems
        .filter(n => !this.mobilePrimary.includes(n.name))
        .map(n => ({ name: `${n.icon} ${n.label}`, routeName: n.name }))
    }
  },
  methods: {
    navByName (name) {
      return this.navItems.find(n => n.name === name) || {}
    },
    openCapture () {
      this.$refs.capture.open()
    },
    openDiscovery () {
      this.$refs.discovery.open()
    },
    onSelectMore (action) {
      this.$router.push({ name: action.routeName })
      this.showMore = false
    }
  }
}
</script>

<style scoped>
.trip-detail-layout {
  min-height: 100%;
  padding-bottom: var(--tabbar-height);
}
.top-bar {
  position: sticky;
  top: 0;
  z-index: 20;
  height: var(--topbar-height);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid var(--color-border);
}
.back-btn {
  font-size: 24px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.top-info { flex: 1; min-width: 0; }
.top-name { font-weight: 700; font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.top-sub { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.icon-btn { border: none; background: none; font-size: 18px; padding: 6px; }

.body { display: flex; }
.desktop-nav { display: none; }
.content { flex: 1; min-width: 0; }

.mobile-tabbar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  height: var(--tabbar-height);
  display: flex;
  background: #fff;
  border-top: 1px solid var(--color-border);
  z-index: 30;
}
.tab-item {
  flex: 1;
  border: none;
  background: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 10px;
  color: var(--color-text-dim);
}
.tab-item .icon { font-size: 18px; }
.tab-item.active { color: var(--color-primary-dark); font-weight: 700; }

.fab {
  position: fixed;
  right: 18px;
  bottom: calc(var(--tabbar-height) + 16px);
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  background: var(--color-primary);
  color: #fff;
  font-size: 26px;
  box-shadow: 0 6px 16px rgba(255, 143, 107, 0.45);
  z-index: 25;
}

.popup-title { padding: 16px; font-weight: 700; font-size: 16px; border-bottom: 1px solid var(--color-border); }

@media (min-width: 960px) {
  .trip-detail-layout { padding-bottom: 0; }
  .desktop-nav {
    display: flex;
    flex-direction: column;
    width: var(--nav-width);
    flex-shrink: 0;
    padding: 16px 10px;
    border-right: 1px solid var(--color-border);
    min-height: calc(100vh - var(--topbar-height));
    background: #fff;
  }
  .nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--radius-md);
    font-size: 14px;
    color: var(--color-text-dim);
  }
  .nav-item .icon { font-size: 16px; }
  .nav-item.active, .nav-item:hover { background: #fff2ea; color: var(--color-primary-dark); font-weight: 700; }
  .mobile-tabbar { display: none; }
  .fab { right: 32px; bottom: 32px; }
}
</style>
